import AppButton from '@nxsys/components/AppButton';
import AppIconButton from '@nxsys/components/AppIconButton';
import { IconDelete, IconEdit } from '@nxsys/components/AppIconSvg';
import AppSwitch from '@nxsys/components/AppSwitch';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { EeErPensionMethodEnum } from '@nxsys/constants/AppEnums';
import {
  useDeleteEmployeeIrelandPrivatePension,
  useGetEmployeeIrelandPrivatePension,
  useToggleActiveEmployeeIrelandPrivatePension,
} from '@nxsys/hooks/api/employee/useEmployeeApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { IEmployeeIrelandPrivatePension } from '@nxsys/types/models/pension';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { showMessage } from '@nxsys/utils/message';
import { Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import EmployeePrivatePensionAddModal from './EmployeePrivatePensionAddModal';
import EmployeePrivatePensionEditModal from './EmployeePrivatePensionEditModal';

type Props = {
  subcontractorId: string;
  isReadOnly?: boolean;
};

const EmployeePrivatePensionTab: React.FC<Props> = ({
  subcontractorId,
  isReadOnly,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const {
    value: settings,
    loading,
    run: fetchSettings,
  } = useGetEmployeeIrelandPrivatePension();

  const { run: deleteSetting } = useDeleteEmployeeIrelandPrivatePension();
  const { run: toggleActive } =
    useToggleActiveEmployeeIrelandPrivatePension();

  useEffect(() => {
    fetchSettings(subcontractorId);
  }, [subcontractorId]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] =
    useState<IEmployeeIrelandPrivatePension | null>(null);

  const formatContribution = (
    value: number | string,
    type?: string,
  ) => {
    if (value === null || value === undefined || value === '') return '-';
    if (type === EeErPensionMethodEnum.PERCENTAGE) return `${value}%`;
    return formatMoney(
      typeof value === 'string' ? parseFloat(value) : value,
      showCurrencyByCountry(payrollCountry),
    );
  };

  const handleToggleActive = async (record: IEmployeeIrelandPrivatePension) => {
    const res = await toggleActive(subcontractorId, record.id);
    if (res) {
      showMessage({ type: 'success', content: messages['common.updateSuccess'] });
      fetchSettings(subcontractorId);
    }
  };

  const handleDelete = (record: IEmployeeIrelandPrivatePension) => {
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        await deleteSetting(subcontractorId, record.id);
        showMessage({ type: 'success', content: messages['common.deleteSuccess'] });
        fetchSettings(subcontractorId);
      },
    });
  };

  const handleEdit = (record: IEmployeeIrelandPrivatePension) => {
    setSelectedSetting(record);
    setEditModalOpen(true);
  };

  const columns: ColumnsType<IEmployeeIrelandPrivatePension> = [
    ...(!isReadOnly
      ? [
          {
            key: 'actions',
            title: messages['table.actions'],
            fixed: 'left' as const,
            align: 'left' as const,
            width: 120,
            render: (_: unknown, record: IEmployeeIrelandPrivatePension) => (
              <ul className="table-actions flex items-center justify-start">
                <li>
                  <AppIconButton
                    title={messages['common.edit']}
                    icon={<IconEdit />}
                    onClick={() => handleEdit(record)}
                  />
                </li>
                <li>
                  <AppIconButton
                    title={messages['common.delete']}
                    icon={<IconDelete />}
                    onClick={() => handleDelete(record)}
                  />
                </li>
              </ul>
            ),
          },
        ]
      : []),
    {
      key: 'isActive',
      title: messages['common.active'] ?? 'Active',
      width: 100,
      render: (_: unknown, record: IEmployeeIrelandPrivatePension) =>
        isReadOnly ? (
          <Tag color={record.isActive ? 'green' : 'red'}>
            {record.isActive ? 'Active' : 'Inactive'}
          </Tag>
        ) : (
          <AppSwitch
            checked={record.isActive}
            onChange={() => handleToggleActive(record)}
          />
        ),
    },
    {
      key: 'type',
      title: messages['common.type'],
      width: 120,
      render: (_: unknown, record: IEmployeeIrelandPrivatePension) => (
        <Tag color="cyan">
          {record.companyPensionSetting?.type || record.type}
        </Tag>
      ),
    },
    {
      key: 'name',
      title: messages['common.name'],
      width: 150,
      render: (_: unknown, record: IEmployeeIrelandPrivatePension) =>
        record.companyPensionSetting?.name || record.name || '-',
    },
    {
      key: 'reference',
      title: messages['common.reference'],
      width: 150,
      render: (_: unknown, record: IEmployeeIrelandPrivatePension) =>
        record.companyPensionSetting?.reference || record.reference || '-',
    },
    {
      key: 'employee',
      title: messages['common.employee'],
      width: 120,
      render: (_: unknown, record: IEmployeeIrelandPrivatePension) =>
        formatContribution(record.eePensionValue, record.eePensionType),
    },
    {
      key: 'employer',
      title: messages['common.employer'],
      width: 120,
      render: (_: unknown, record: IEmployeeIrelandPrivatePension) =>
        formatContribution(record.erPensionValue, record.erPensionType),
    },
    {
      key: 'avc',
      title: messages['common.avc'] ?? 'AVC',
      width: 120,
      render: (_: unknown, record: IEmployeeIrelandPrivatePension) =>
        formatContribution(record.avcPensionValue, record.avcPensionType),
    },
  ];

  return (
    <div className="EmployeePrivatePensionTab">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[14px] font-semibold">
          {messages['common.privatePensionSchemes'] ?? 'Private Pension Schemes'}
        </div>
        {!isReadOnly && (
          <AppButton
            name={messages['common.addPensionScheme'] ?? 'Add Pension Scheme'}
            leftIcon={
              <Icons.IconPlus color={defaultTheme.theme.palette.white} />
            }
            onClick={() => setAddModalOpen(true)}
          />
        )}
      </div>
      <AppTableContainer
        rowKey="id"
        columns={columns}
        data={settings ?? []}
        loading={loading}
        pagination={false}
        page={1}
        total={settings?.length ?? 0}
        pageSize={settings?.length ?? 0}
        scroll={{ x: 1000 }}
      />
      <EmployeePrivatePensionAddModal
        isOpen={addModalOpen}
        toggle={() => setAddModalOpen(false)}
        subcontractorId={subcontractorId}
        onSuccess={() => fetchSettings(subcontractorId)}
      />
      {selectedSetting && editModalOpen && (
        <EmployeePrivatePensionEditModal
          isOpen={editModalOpen}
          toggle={() => setEditModalOpen(false)}
          subcontractorId={subcontractorId}
          data={selectedSetting}
          onSuccess={() => fetchSettings(subcontractorId)}
        />
      )}
    </div>
  );
};

export default EmployeePrivatePensionTab;
